import React from "react";
import { CreateScenarioCommandRole, RoleRO, UpdateScenarioCommandRole } from "../../../api/api";
import PopupDialog from "../../../components/Form/PopupDialog";
import SettingsTextField from "../../../components/Settings/Old/Form/SettingsTextField";
import VariableForm from "../../../components/Variables/VariableForm";
import { VariableDefinition } from "../../../components/Variables/VariableDefinitionForm";
import { makeStyles } from "@mui/styles";
import { DeleteOutline } from "@mui/icons-material";
import QAIconButton from "../../../components/Design/Controls/QAIconButton";
import QAButton from "../../../components/Design/Controls/QAButton";
import TextInputDialog from "../../../components/Hooks/Dialog/TextInputDialog";
import useDialog from "../../../components/Hooks/Dialog/useDialog";
import QAButtonRow from "../../../components/Design/Layout/QAButtonRow";

interface Props {
    open: boolean;
    role: CreateScenarioCommandRole & UpdateScenarioCommandRole;
    onUpdate: (roleGroup: CreateScenarioCommandRole & UpdateScenarioCommandRole) => void;
    availableRoles: RoleRO[];
    disabled: boolean;
    scenarioVariables: VariableDefinition[];
    onClose: () => void;
}

const useStyles = makeStyles({
    paper: {
        minWidth: "90vw"
    },
    row: {
        display: "flex",
        flexDirection: "row",
        gap: "0.5rem"
    },
    name: {
        flex: "0 0 200px"
    },
    delete: {
        marginBottom: "12px",
        borderColor: "indianred",
        color: "indianred"
    }
});

const CreateExecutionDialog: React.FC<Props> = props => {
    const classes = useStyles();
    const dialog = useDialog();

    const {
        open,
        role,
        onUpdate,
        availableRoles,
        disabled,
        scenarioVariables,
        onClose
    } = props;

    return (
        <PopupDialog
            open={open}
            onFirst={onClose}
            firstTitle="Fertig"
            paperClassName={classes.paper}
            title="Instanzen bearbeiten">

            <QAButtonRow alignRight marginBottom>

                <QAButton
                    light
                    size="tiny"
                    disabled={disabled}
                    title="Instanz hinzufügen"
                    onClicked={() => onUpdate({
                        ...role,
                        instances: [
                            ...role.instances,
                            {
                                name: (availableRoles.find(ar => ar.id === role.roleId)?.name ?? "Neue Instanz") + " " + (role.instances.length + 1),
                                variables: {}
                            }
                        ]
                    })} />

                <QAButton
                    light
                    size="tiny"
                    disabled={disabled}
                    title="Mehrere Instanzen hinzufügen"
                    onClicked={async () => {
                        const response = await dialog.open(TextInputDialog, {
                            title: "Mehrere Instanzen hinzufügen",
                            question: "Wie viele Instanzen möchten Sie hinzufügen?",
                            label: "Anzahl",
                            acceptButtonTitle: "Hinzufügen"
                        });
                        if (!response.accepted) {
                            return;
                        }

                        const count = parseInt(response.result);
                        if (isNaN(count) || count <= 0) {
                            return;
                        }

                        const newInstances = [...role.instances];
                        for (let i = 0; i < count; i++) {
                            newInstances.push({
                                name: (availableRoles.find(ar => ar.id === role.roleId)?.name ?? "Neue Instanz") + " " + (newInstances.length + 1),
                                variables: {}
                            });
                        }
                        onUpdate({
                            ...role,
                            instances: newInstances
                        });
                    }} />

            </QAButtonRow>

            <div>
                {role.instances.map((instance, instanceIndex) => (
                    <div key={instanceIndex} className={classes.row}>

                        <SettingsTextField
                            label="Name"
                            className={classes.name}
                            value={instance.name ?? ""}
                            onChanged={value => onUpdate({
                                ...role,
                                instances: role.instances.map((ir, ii) => ii === instanceIndex ? {
                                    ...ir,
                                    name: value
                                } : ir)
                            })} />
                        <VariableForm
                            rowMode
                            variables={availableRoles.find(ar => ar.id === role.roleId)?.variables ?? []}
                            disabled={disabled}
                            shadowedVariables={scenarioVariables}
                            onValidate={console.log}
                            onUpdate={(name, value) => onUpdate({
                                ...role,
                                instances: role.instances.map(i => i === instance ? {
                                    ...i,
                                    variables: {
                                        ...i.variables,
                                        [name]: value
                                    }
                                } : i)
                            })}
                            values={instance.variables} />
                        <QAIconButton
                            light
                            noTabFocus
                            icon={<DeleteOutline />}
                            size="small"
                            className={classes.delete}
                            onClicked={() => onUpdate({
                                ...role,
                                instances: role.instances.filter((_, ii) => ii !== instanceIndex)
                            })} />

                    </div>
                ))}
            </div>

        </PopupDialog>
    );
};

export default CreateExecutionDialog;
